import { getRegistryURL } from "./Registry";
import { buildGetOptions, buildPostOptions, fetchWithErrorHandling } from "../FetchUtils";
import { useAuthUser } from "react-auth-kit";

export interface IWorkflowRunSnapshot {
  runId: number;
  status: string;
  conclusion: string | null;
  runStartedAt: string;
  updatedAt: string;
  headBranch: string;
  durationMs: number;
}

export interface IWorkflowMetricDataPoint {
  workflowName: string;
  timestamp: number;
  queuedCount: number;
  inProgressCount: number;
  failedCountLastHour: number;
  successCountLastHour: number;
  avgDurationMs: number;
  currentRuns?: IWorkflowRunSnapshot[];
  recentlyCompletedRuns?: IWorkflowRunSnapshot[];
  successRate: number;
  ttl: number;
}

export interface IWorkflowMetricsResponse {
  workflowName: string;
  dataPoints: IWorkflowMetricDataPoint[];
}

export interface IWorkflowSummaryEntry {
  workflowName: string;
  displayName: string;
  latestDataPoint: IWorkflowMetricDataPoint | null;
}

export interface IWorkflowSummaryResponse {
  workflows: IWorkflowSummaryEntry[];
}

export interface IWorkflowFailuresResponse {
  dataPoints: IWorkflowMetricDataPoint[];
}

export interface IRunnerInfo {
  id: number;
  name: string;
  status: string;
  busy: boolean;
  labels: string[];
}

export interface IRepoRunnerStatus {
  repo: string;
  total: number;
  online: number;
  offline: number;
  busy: number;
  idle: number;
  runners: IRunnerInfo[];
}

export interface IFCB2PoolStatus {
  groupId: string;
  runId: number;
  status: string;
  conclusion: string;
  durationMs: number;
  totalRunners: number;
  online: number;
  offline: number;
  busy: number;
  idle: number;
  runners: IRunnerInfo[];
  error?: string;
}

export interface IRunnersStatusResponse {
  fetchedAt: number;
  repos: IRepoRunnerStatus[];
  fcb2Pools: IFCB2PoolStatus[];
}

const BASE_PATH = "/v1/registry/workflows-monitoring";

export const getWorkflowMetrics = async (
  accessToken: string,
  workflowName: string,
  from?: number,
  to?: number,
  limit?: number,
): Promise<IWorkflowMetricsResponse> => {
  const body: Record<string, unknown> = { workflowName };
  if (from !== undefined) body.from = from;
  if (to !== undefined) body.to = to;
  if (limit !== undefined) body.limit = limit;

  return fetchWithErrorHandling(getRegistryURL() + `${BASE_PATH}/metrics`, buildPostOptions(accessToken, body));
};

export const getWorkflowSummary = async (accessToken: string): Promise<IWorkflowSummaryResponse> => {
  return fetchWithErrorHandling(getRegistryURL() + `${BASE_PATH}/summary`, buildGetOptions(accessToken));
};

export const getWorkflowFailures = async (
  accessToken: string,
  from?: number,
  to?: number,
  limit?: number,
): Promise<IWorkflowFailuresResponse> => {
  const body: Record<string, unknown> = {};
  if (from !== undefined) body.from = from;
  if (to !== undefined) body.to = to;
  if (limit !== undefined) body.limit = limit;

  return fetchWithErrorHandling(getRegistryURL() + `${BASE_PATH}/failures`, buildPostOptions(accessToken, body));
};

export const getRunnersStatus = async (accessToken: string): Promise<IRunnersStatusResponse> => {
  return fetchWithErrorHandling(getRegistryURL() + `${BASE_PATH}/runners`, buildGetOptions(accessToken));
};

export const forceCancelWorkflowRun = async (accessToken: string, repo: string, runId: number): Promise<void> => {
  await fetchWithErrorHandling(
    getRegistryURL() + `${BASE_PATH}/force-cancel`,
    buildPostOptions(accessToken, { repo, runId }),
  );
};

export const useWorkflowsMonitoringService = () => {
  const authUser = useAuthUser();

  return {
    getWorkflowMetrics: (workflowName: string, from?: number, to?: number, limit?: number) =>
      getWorkflowMetrics(authUser()?.access_token, workflowName, from, to, limit),
    getWorkflowSummary: () => getWorkflowSummary(authUser()?.access_token),
    getWorkflowFailures: (from?: number, to?: number, limit?: number) =>
      getWorkflowFailures(authUser()?.access_token, from, to, limit),
    getRunnersStatus: () => getRunnersStatus(authUser()?.access_token),
    forceCancelWorkflowRun: (repo: string, runId: number) => forceCancelWorkflowRun(authUser()?.access_token, repo, runId),
  };
};
